<template>
    <el-main class="customer-workbench">
        <div class="control-bar">
            <span class="title">客户服务</span>
            <div class="stat-cards">
                <div class="stat-card">
                    <span class="stat-label">排队待接入</span>
                    <span class="stat-value" :class="{ warn: stat.waiting > 0 }">{{ stat.waiting }}</span>
                </div>
                <div class="stat-card">
                    <span class="stat-label">人工服务中</span>
                    <span class="stat-value doing">{{ stat.manual }}</span>
                </div>
                <div class="stat-card">
                    <span class="stat-label">未读消息</span>
                    <span class="stat-value" :class="{ warn: stat.unread > 0 }">{{ stat.unread }}</span>
                </div>
                <div class="stat-card">
                    <span class="stat-label">近30天会话</span>
                    <span class="stat-value">{{ stat.sessions }}</span>
                </div>
            </div>
            <el-form :inline="true" size="small">
                <el-form-item>
                    <el-input v-model="filters.wd" placeholder="昵称/手机号/UID" clearable
                        @clear="getLists(1)" @keyup.enter.native="getLists(1)"></el-input>
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" icon="el-icon-search" @click="getLists(1)">查询</el-button>
                </el-form-item>
                <el-form-item>
                    <el-button icon="el-icon-refresh" @click="getLists(1)">刷新</el-button>
                </el-form-item>
            </el-form>
        </div>

        <el-tabs v-model="activeTab" @tab-click="handleTab" class="session-tabs">
            <el-tab-pane :label="'排队待接入(' + stat.waiting + ')'" name="0"></el-tab-pane>
            <el-tab-pane :label="'人工服务中(' + stat.manual + ')'" name="1"></el-tab-pane>
            <el-tab-pane label="全部会话" name=""></el-tab-pane>
        </el-tabs>

        <div class="workbench">
            <!-- 左侧会话列表 -->
            <div class="session-list" v-loading="listLoading">
                <div v-if="sessions.length === 0" class="empty-tip">暂无会话</div>
                <div v-for="row in sessions" :key="row.uid" class="session-item"
                    :class="{ active: current && current.uid === row.uid }" @click="selectSession(row)">
                    <div class="session-top">
                        <span class="session-name">{{ row.nickname || ('用户' + row.uid) }}</span>
                        <span class="session-time">{{ row.last_dateline | timeTodate('m-d H:i') }}</span>
                    </div>
                    <div class="session-meta">
                        <el-tag v-if="row.status === 1" size="mini" type="success">{{ row.staff || '人工中' }}</el-tag>
                        <el-tag v-else-if="row.status === 0" size="mini" type="warning">
                            {{ reasonText(row.reason) }}·等{{ waitMinutes(row.queue_dateline) }}分钟
                        </el-tag>
                        <el-tag v-if="row.last_from_service" size="mini" type="info">待用户回复</el-tag>
                        <span class="session-mobile">{{ maskMobile(row.mobile) }}</span>
                    </div>
                    <div class="session-bottom">
                        <span class="session-last">{{ row.last_ctype === 2 ? '[图片]' : row.last_content }}</span>
                        <el-badge v-if="row.unread > 0" :value="row.unread" class="session-unread"></el-badge>
                    </div>
                </div>
                <el-pagination small background layout="prev, pager, next" :page-size="offset"
                    :current-page="page" :total="totals" @current-change="getLists" class="session-page">
                </el-pagination>
            </div>

            <!-- 右侧对话面板 -->
            <div class="chat-panel">
                <template v-if="current">
                    <div class="chat-header">
                        <div class="chat-user">
                            <strong>{{ current.nickname || ('用户' + current.uid) }}</strong>
                            <span class="uid">UID：{{ current.uid }}</span>
                            <span class="meta">{{ maskMobile(current.mobile) }}</span>
                            <span class="meta">{{ current.gender === 1 ? '男' : (current.gender === 2 ? '女' : '未填')
                                }}</span>
                            <span class="meta">{{ current.city || '-' }}</span>
                            <span class="meta">红豆 {{ current.credits }}</span>
                            <el-tag v-if="current.isvip === 1" size="mini">会员</el-tag>
                        </div>
                        <div class="chat-actions">
                            <el-button v-if="current.status !== 1" type="primary" size="mini"
                                @click="takeover">确认人工介入</el-button>
                            <el-button v-if="current.status === 1" size="mini" @click="release">结束人工</el-button>
                            <el-button v-if="current.status === 0" size="mini" type="text"
                                @click="ignore">转回智能客服</el-button>
                        </div>
                    </div>

                    <div class="chat-state" v-if="current.status === 0">
                        用户已申请人工客服（{{ reasonText(current.reason) }}），当前排队第
                        {{ queue.position }} 位，前面还有 {{ Math.max(queue.queue_len - queue.position, 0) }} 位。
                        接入后智能客服将停止回复。
                    </div>

                    <div class="chat-body" ref="chatBody" v-loading="msgLoading">
                        <div v-for="item in messages" :key="item.id" class="chat-row"
                            :class="{ self: item.source_uid === 1 }">
                            <div class="chat-avatar">{{ item.source_uid === 1 ? '客服' : '用户' }}</div>
                            <div class="chat-bubble" :class="{ 'bubble-img': item.ctype === 2 }">
                                <el-image v-if="item.ctype === 2" class="chat-image" :src="imgUrl(item.content)"
                                    :preview-src-list="[imgUrl(item.content)]" fit="cover"></el-image>
                                <span v-else class="chat-text">{{ item.content }}</span>
                                <span class="chat-time">{{ item.dateline | timeTodate('m-d H:i:s') }}</span>
                            </div>
                        </div>
                        <div v-if="messages.length === 0" class="empty-tip">暂无消息记录</div>
                    </div>

                    <div class="chat-input">
                        <el-input v-model="content" type="textarea" :rows="2" resize="none"
                            placeholder="输入回复内容，Ctrl + Enter 发送" @keydown.native.ctrl.enter="reply"></el-input>
                        <div class="input-footer">
                            <span class="tip">以「在线客服」身份发送，用户端实时收到</span>
                            <el-button type="primary" size="small" :disabled="!content.trim() || sending"
                                @click="reply">发送</el-button>
                        </div>
                    </div>
                </template>
                <div v-else class="empty-panel">请选择左侧会话查看聊天记录<br />
                    <span class="sub">排队待接入的会话排在最前面，点击「确认人工介入」后智能客服停止回复</span>
                </div>
            </div>
        </div>
    </el-main>
</template>

<script>
import {
    httpRequest,
    aliUrl,
    getCookie
} from '@/utils/app'

export default {
    data() {
        return {
            admin: {},
            activeTab: '0',
            filters: {
                wd: ''
            },
            stat: {
                waiting: 0,
                manual: 0,
                unread: 0,
                sessions: 0
            },
            sessions: [],
            totals: 0,
            page: 1,
            offset: 15,
            listLoading: false,
            // 当前会话
            current: null,
            queue: {
                status: 2,
                queue_len: 0,
                position: 0
            },
            messages: [],
            msgTotals: 0,
            msgLoading: false,
            content: '',
            sending: false,
            listTimer: null,
            msgTimer: null
        }
    },
    created() {
        this.admin = getCookie('admin')
        this.getLists(1)
    },
    mounted() {
        // 排队中的用户每分钟由服务端推送队列长度，工作台按固定频率拉取即可
        this.listTimer = setInterval(() => this.getLists(this.page, true), 15000)
        this.msgTimer = setInterval(() => {
            if (this.current) this.getMessages(this.current.uid, true)
        }, 5000)
    },
    beforeDestroy() {
        clearInterval(this.listTimer)
        clearInterval(this.msgTimer)
    },
    methods: {
        getLists(page, silent) {
            if (!silent) this.listLoading = true
            let params = {
                action: 'customer_lists',
                page: page || 1,
                offset: this.offset
            }
            if (this.activeTab !== '') params.status = this.activeTab
            if (this.filters.wd) params.wd = this.filters.wd

            httpRequest(params).then(response => {
                this.listLoading = false
                if (response.error === false) {
                    this.page = page || 1
                    this.sessions = response.data || []
                    this.totals = parseInt(response.totals)
                    this.stat = response.stat || this.stat
                } else if (!silent) {
                    this.$message({ type: 'error', message: response.msg || '加载失败' })
                }
            })
        },
        handleTab() {
            this.current = null
            this.messages = []
            this.getLists(1)
        },
        selectSession(row) {
            this.current = row
            this.messages = []
            this.getMessages(row.uid)
        },
        getMessages(uid, silent) {
            if (!uid) return
            if (!silent) this.msgLoading = true
            httpRequest({
                action: 'customer_detail',
                uid: uid,
                page: 1,
                offset: 100
            }).then(response => {
                this.msgLoading = false
                if (response.error === false) {
                    this.messages = response.data || []
                    this.msgTotals = parseInt(response.totals)
                    this.queue = response.queue || this.queue
                    // 服务端已回写 readtime，列表未读数下一次刷新归零
                    if (this.current) this.current.unread = 0
                    this.$nextTick(this.scrollBottom)
                }
            })
        },
        scrollBottom() {
            const el = this.$refs.chatBody
            if (el) el.scrollTop = el.scrollHeight
        },
        reply() {
            const content = this.content.trim()
            if (!content || !this.current) return
            this.sending = true
            httpRequest({
                action: 'customer_reply',
                uid: this.current.uid,
                content: content
            }, 'POST').then(response => {
                this.sending = false
                if (response.error === false) {
                    this.content = ''
                    this.current.status = 1
                    this.getMessages(this.current.uid, true)
                    this.getLists(this.page, true)
                } else {
                    this.$message({ type: 'error', message: response.msg || '发送失败' })
                }
            })
        },
        takeover() {
            this.action('customer_takeover', '已接入人工服务')
        },
        release() {
            this.action('customer_release', '已结束人工服务')
        },
        ignore() {
            this.action('customer_ignore', '已转回智能客服')
        },
        action(name, okMsg) {
            httpRequest({
                action: name,
                uid: this.current.uid
            }, 'POST').then(response => {
                if (response.error === false) {
                    this.$message({ type: 'success', message: okMsg })
                    // 状态变更后同步刷新队列快照：takeover 后 status=1，release/ignore 后 status=2
                    this.current.status = name === 'customer_takeover' ? 1 : 2
                    this.queue = response.queue || this.queue
                    this.getMessages(this.current.uid, true)
                    this.getLists(this.page, true)
                } else {
                    this.$message({ type: 'error', message: response.msg || '操作失败' })
                }
            })
        },
        imgUrl(path) {
            if (!path) return ''
            return /^https?:\/\//.test(path) ? path : aliUrl + path
        },
        // 手机号脱敏（不用全局 filter：资料缺失时值为空会抛异常）
        maskMobile(mobile) {
            if (!mobile) return '-'
            return String(mobile).substring(0, 3) + '****' + String(mobile).substring(7, 11)
        },
        reasonText(reason) {
            return {
                1: 'AI无法处理',
                2: '图片消息',
                3: '主动申请'
            }[reason] || '待接入'
        },
        waitMinutes(dateline) {
            if (!dateline) return 0
            return Math.max(1, Math.round((Date.now() - dateline) / 60000))
        }
    }
}
</script>

<style scoped>
.customer-workbench {
    display: flex;
    flex-direction: column;
}

.control-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.stat-cards {
    display: flex;
    gap: 12px;
    margin: 0 20px 0 0;
}

.stat-card {
    min-width: 96px;
    padding: 6px 12px;
    border-radius: 8px;
    background: linear-gradient(135deg, #fdeef4, #f9f3fb);
    display: flex;
    flex-direction: column;
}

.stat-label {
    font-size: 12px;
    color: #8a8a9a;
}

.stat-value {
    font-size: 20px;
    font-weight: 600;
    color: #dc6b91;
}

.stat-value.warn {
    color: #e6a23c;
}

.stat-value.doing {
    color: #67c23a;
}

.session-tabs {
    margin-bottom: 8px;
}

.workbench {
    flex: 1;
    display: flex;
    gap: 12px;
    min-height: 560px;
}

.session-list {
    width: 300px;
    flex-shrink: 0;
    border: 1px solid #f0e2ea;
    border-radius: 8px;
    background: #fff;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.session-item {
    padding: 10px 12px;
    border-bottom: 1px solid #f5eef2;
    cursor: pointer;
}

.session-item:hover,
.session-item.active {
    background: #fdf0f5;
}

.session-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.session-name {
    font-weight: 600;
    color: #333;
}

.session-time {
    font-size: 12px;
    color: #a5a5b5;
}

.session-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    font-size: 12px;
    color: #8a8a9a;
}

.session-mobile {
    margin-left: auto;
}

.session-bottom {
    display: flex;
    align-items: center;
    margin-top: 4px;
}

.session-last {
    flex: 1;
    font-size: 13px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.session-page {
    margin-top: auto;
    padding: 8px;
    text-align: center;
}

.chat-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    border: 1px solid #f0e2ea;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
}

.chat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 16px;
    border-bottom: 1px solid #f5eef2;
}

.chat-user {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #444;
}

.chat-user .uid,
.chat-user .meta {
    color: #8a8a9a;
    font-size: 12px;
}

.chat-state {
    padding: 8px 16px;
    background: #fdf6ec;
    color: #b88230;
    font-size: 13px;
}

.chat-body {
    flex: 1;
    padding: 16px;
    overflow-y: auto;
    background: #faf7f9;
}

.chat-row {
    display: flex;
    margin-bottom: 14px;
}

.chat-row.self {
    flex-direction: row-reverse;
}

.chat-avatar {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 6px;
    background: #dc6b91;
    color: #fff;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.chat-row:not(.self) .chat-avatar {
    background: #7a7f9c;
}

.chat-bubble {
    max-width: 60%;
    margin: 0 10px;
    padding: 8px 12px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.chat-row.self .chat-bubble {
    background: #d2f0d7;
}

.bubble-img {
    padding: 6px;
}

.chat-image {
    width: 180px;
    border-radius: 6px;
    display: block;
}

.chat-text {
    color: #333;
    word-break: break-all;
    white-space: pre-wrap;
}

.chat-time {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #a5a5b5;
}

.chat-input {
    border-top: 1px solid #f5eef2;
    padding: 10px 16px;
}

.input-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
}

.input-footer .tip {
    font-size: 12px;
    color: #a5a5b5;
}

.empty-tip {
    padding: 24px;
    text-align: center;
    color: #a5a5b5;
    font-size: 13px;
}

.empty-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #8a8a9a;
}

.empty-panel .sub {
    margin-top: 8px;
    font-size: 12px;
    color: #b5b5c2;
}
</style>
